// Turns a FHIR patient record into the same extraction shape the document
// import produces, so both paths share normalizeExtraction, buildProposals and
// the review dialog. Nothing is written until the patient approves each row.

import { normalizeExtraction } from "services/healthRecordImportService";

const RXNORM_SYSTEM = "http://www.nlm.nih.gov/research/umls/rxnorm";
const MAX_PAGES = 5;

const entriesOf = (bundle) =>
  (bundle?.entry || []).map((e) => e.resource).filter(Boolean);

/** A FHIR dateTime may carry a time; the record stores calendar days. */
const asDay = (value) => {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value || ""));
  return match ? match[1] : null;
};

/** CodeableConcept → the most human of the names it carries. */
const conceptText = (concept) =>
  concept?.text ||
  concept?.coding?.find((c) => c.display)?.display ||
  "";

const rxcuiOf = (concept) =>
  concept?.coding?.find((c) => c.system === RXNORM_SYSTEM && /^\d+$/.test(c.code || ""))
    ?.code || null;

async function searchAll({ fhirBaseUrl, accessToken, resourceType, patientId }) {
  const collected = [];
  let url = `${fhirBaseUrl}/${resourceType}?patient=${encodeURIComponent(patientId)}`;
  for (let page = 0; page < MAX_PAGES && url; page += 1) {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/fhir+json" },
    });
    if (!response.ok) {
      throw new Error(`${resourceType} could not be read (HTTP ${response.status}).`);
    }
    const bundle = await response.json();
    collected.push(...entriesOf(bundle));
    url = bundle?.link?.find((l) => l.relation === "next")?.url || null;
  }
  return collected;
}

const CONDITION_STATUS = {
  active: "active",
  recurrence: "active",
  relapse: "active",
  remission: "controlled",
  inactive: "resolved",
  resolved: "resolved",
};

/**
 * MedicationRequest → one medication course.
 *
 * Past courses are kept: a medication stopped last year is exactly the history
 * the record is missing. Only drafts and corrections are dropped, since those
 * never described something the patient took.
 */
function medicationFrom(resource, sourceLabel) {
  const concept =
    resource.medicationCodeableConcept ||
    resource.contained?.find((c) => c.resourceType === "Medication")?.code;
  const name = conceptText(concept) || resource.medicationReference?.display || "";
  const instruction = resource.dosageInstruction?.[0] || {};
  const doseQuantity = instruction.doseAndRate?.[0]?.doseQuantity;
  const period =
    instruction.timing?.repeat?.boundsPeriod || resource.dispenseRequest?.validityPeriod || {};

  return {
    name,
    rxcui: rxcuiOf(concept),
    dosage: doseQuantity?.value
      ? `${doseQuantity.value} ${doseQuantity.unit || doseQuantity.code || ""}`.trim()
      : "",
    // Epic and most EHRs put the whole sig in text ("Take 1 tablet twice daily").
    frequency: instruction.text || conceptText(instruction.timing?.code) || "",
    startDate: asDay(period.start) || asDay(resource.authoredOn),
    endDate: asDay(period.end),
    notes: "",
    sourceDocument: sourceLabel,
  };
}

/** Bundles → the extraction shape, before normalizeExtraction validates it. */
export function buildExtraction({ patient, allergies, conditions, medications }, sourceLabel) {
  const name = patient?.name?.[0];
  return {
    demographics: {
      patientName:
        name?.text ||
        [name?.given?.join(" "), name?.family].filter(Boolean).join(" ") ||
        null,
      dateOfBirth: asDay(patient?.birthDate),
      sex: ["male", "female"].includes(patient?.gender) ? patient.gender : "other",
      weightKg: null,
      sourceDocument: sourceLabel,
    },
    allergies: (allergies || [])
      .filter((r) => r.clinicalStatus?.coding?.[0]?.code !== "inactive")
      .map((resource) => ({
        allergen: conceptText(resource.code),
        type: resource.category?.includes("medication") ? "drug" : "other",
        reaction: (resource.reaction || [])
          .flatMap((r) => (r.manifestation || []).map(conceptText))
          .filter(Boolean)
          .join(", "),
        notes: resource.note?.[0]?.text || "",
        sourceDocument: sourceLabel,
      })),
    conditions: (conditions || []).map((resource) => ({
      condition: conceptText(resource.code),
      status: CONDITION_STATUS[resource.clinicalStatus?.coding?.[0]?.code] || "active",
      notes: "",
      sourceDocument: sourceLabel,
    })),
    surgeries: [],
    medications: (medications || [])
      .filter((r) => !["draft", "entered-in-error"].includes(r.status))
      .map((resource) => medicationFrom(resource, sourceLabel)),
    warnings: [],
  };
}

/**
 * Reads the connected record. One resource failing does not lose the rest —
 * it becomes a warning on the review screen instead, because a partial import
 * the patient can see is more useful than an error page.
 */
export async function fetchRecord({ fhirBaseUrl, accessToken, patientId }, sourceLabel) {
  if (!patientId) {
    throw new Error("The hospital did not say whose record this is.");
  }
  const patientResponse = await fetch(
    `${fhirBaseUrl}/Patient/${encodeURIComponent(patientId)}`,
    { headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/fhir+json" } }
  );
  if (!patientResponse.ok) {
    throw new Error(`Your record could not be read (HTTP ${patientResponse.status}).`);
  }
  const patient = await patientResponse.json();

  const warnings = [];
  const read = async (resourceType) => {
    try {
      return await searchAll({ fhirBaseUrl, accessToken, resourceType, patientId });
    } catch (error) {
      warnings.push(`${error.message} Nothing from it was imported.`);
      return [];
    }
  };

  const [allergies, conditions, medications] = await Promise.all([
    read("AllergyIntolerance"),
    read("Condition"),
    read("MedicationRequest"),
  ]);

  const extraction = normalizeExtraction(
    buildExtraction({ patient, allergies, conditions, medications }, sourceLabel)
  );
  extraction.warnings = [...warnings, ...extraction.warnings];
  return extraction;
}
